import React, { useEffect, useState } from "react";
import TopBar from "@/components/TopBar";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { PieChart, TrendingUp, TrendingDown, BarChart3, Target, Award, Clock, DollarSign } from "lucide-react";

function fmt(n, d = 2) {
  if (n == null || Number.isNaN(n)) return "—";
  return Number(n).toLocaleString("en-US", { minimumFractionDigits: d, maximumFractionDigits: d });
}

export default function Portfolio() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/account/portfolio")
      .then(({ data }) => setData(data))
      .catch((e) => console.error(formatApiError(e.response?.data?.detail) || e.message))
      .finally(() => setLoading(false));
  }, []);

  const symbolEntries = data ? Object.entries(data.symbol_breakdown || {}).sort((a, b) => b[1].pnl - a[1].pnl) : [];
  const totalVolume = data?.total_volume || 0;

  return (
    <div className="min-h-screen bg-app text-white">
      <TopBar />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-6 flex items-center gap-3">
          <PieChart className="w-7 h-7 text-buy" />
          {t("portfolio_title")}
        </h1>

        {loading ? (
          <div className="text-dim text-sm py-12 text-center">{t("loading")}</div>
        ) : !data ? (
          <div className="text-dim text-sm py-12 text-center">{t("no_data")}</div>
        ) : (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <StatCard
                icon={<BarChart3 className="w-5 h-5" />}
                label={t("portfolio_total_trades")}
                value={data.total_trades}
                color="text-blue-400"
              />
              <StatCard
                icon={<Target className="w-5 h-5" />}
                label={t("portfolio_win_rate")}
                value={`%${data.win_rate}`}
                color={data.win_rate >= 50 ? "text-buy" : "text-sell"}
              />
              <StatCard
                icon={<DollarSign className="w-5 h-5" />}
                label={t("trader_total_pnl")}
                value={`${data.total_pnl >= 0 ? "+" : ""}$${fmt(data.total_pnl)}`}
                color={data.total_pnl >= 0 ? "text-buy" : "text-sell"}
              />
              <StatCard
                icon={<Award className="w-5 h-5" />}
                label={t("portfolio_total_volume")}
                value={`$${fmt(totalVolume)}`}
                color="text-yellow-500"
              />
            </div>

            {/* Win/Loss + Best/Worst */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <div className="bg-panel border border-line rounded-sm p-5">
                <h2 className="text-sm font-bold tracking-widest uppercase mb-4 text-blue-400">{t("portfolio_win_loss")}</h2>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-dim uppercase tracking-widest flex items-center gap-2">
                      <TrendingUp className="w-3.5 h-3.5 text-buy" /> {t("portfolio_won")}
                    </span>
                    <span className="font-mono text-sm text-buy">{data.won_trades}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-dim uppercase tracking-widest flex items-center gap-2">
                      <TrendingDown className="w-3.5 h-3.5 text-sell" /> {t("portfolio_lost")}
                    </span>
                    <span className="font-mono text-sm text-sell">{data.lost_trades}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-xs text-dim uppercase tracking-widest flex items-center gap-2">
                      <Clock className="w-3.5 h-3.5" /> {t("open_positions")}
                    </span>
                    <span className="font-mono text-sm">{data.open_positions} (${fmt(data.open_value)})</span>
                  </div>
                  {/* Win rate bar */}
                  <div className="pt-3">
                    <div className="text-[10px] text-dim uppercase tracking-widest mb-1.5">{t("portfolio_win_rate")}</div>
                    <div className="h-2 bg-app rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-buy transition-all duration-500"
                        style={{ width: `${Math.min(100, data.win_rate)}%` }}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-panel border border-line rounded-sm p-5">
                <h2 className="text-sm font-bold tracking-widest uppercase mb-4 text-yellow-500">{t("portfolio_best_worst")}</h2>
                <div className="space-y-4">
                  <div>
                    <div className="text-[10px] text-dim uppercase tracking-widest mb-1">{t("portfolio_best_trade")}</div>
                    <div className="font-mono text-lg text-buy">+${fmt(data.best_trade)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] text-dim uppercase tracking-widest mb-1">{t("portfolio_worst_trade")}</div>
                    <div className="font-mono text-lg text-sell">${fmt(data.worst_trade)}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Symbol Breakdown */}
            <div className="bg-panel border border-line rounded-sm p-5">
              <h2 className="text-sm font-bold tracking-widest uppercase mb-4 text-purple-400">{t("portfolio_asset_distribution")}</h2>
              {symbolEntries.length === 0 ? (
                <div className="text-dim text-xs py-4">{t("portfolio_no_data")}</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-xs font-mono" style={{ borderCollapse: "collapse" }}>
                    <thead>
                      <tr className="text-dim uppercase text-[9px] tracking-widest">
                        <th className="text-left py-2 px-2">{t("portfolio_asset")}</th>
                        <th className="text-right py-2 px-2">{t("portfolio_trades")}</th>
                        <th className="text-right py-2 px-2">{t("portfolio_volume")}</th>
                        <th className="text-right py-2 px-2">{t("pnl")}</th>
                        <th className="text-right py-2 px-2">{t("portfolio_share")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {symbolEntries.map(([sym, s]) => {
                        const volShare = totalVolume > 0 ? (s.volume / totalVolume * 100) : 0;
                        return (
                          <tr key={sym} className="border-t border-subtle/30">
                            <td className="py-2.5 px-2 font-bold">{sym}</td>
                            <td className="py-2.5 px-2 text-right">{s.count}</td>
                            <td className="py-2.5 px-2 text-right">${fmt(s.volume)}</td>
                            <td className={`py-2.5 px-2 text-right ${s.pnl >= 0 ? "text-buy" : "text-sell"}`}>
                              {s.pnl >= 0 ? "+" : ""}${fmt(s.pnl)}
                            </td>
                            <td className="py-2.5 px-2 text-right">
                              <div className="flex items-center justify-end gap-2">
                                <div className="w-16 h-1.5 bg-app rounded-full overflow-hidden">
                                  <div className="h-full bg-purple-500 rounded-full" style={{ width: `${volShare}%` }} />
                                </div>
                                <span className="text-dim">{volShare.toFixed(0)}%</span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, color }) {
  return (
    <div className="bg-panel border border-line rounded-sm p-4">
      <div className={`${color} mb-2`}>{icon}</div>
      <div className="text-[10px] uppercase tracking-widest text-dim mb-1">{label}</div>
      <div className={`font-mono text-lg font-bold ${color}`}>{value}</div>
    </div>
  );
}
